import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "container-fluid",
  style: {"margin-top":"80px","padding-top":"40px"}
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-lg-6" }
const _hoisted_4 = { class: "col-lg-6 text-right align-self-end" }
const _hoisted_5 = {
  key: 3,
  class: "btn btn-outline-secondary mr-1",
  style: {"padding":"0.7em 2em","cursor":"default"},
  disabled: "",
  title: "You cannot edit a submitted plan"
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "form-group mb-4" }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { class: "toolbar" }
const _hoisted_10 = {
  class: "btn-group mr-2",
  role: "group"
}
const _hoisted_11 = { class: "dropdown d-inline mr-2" }
const _hoisted_12 = {
  class: "btn btn-outline-secondary dropdown-toggle",
  type: "button",
  "data-toggle": "dropdown"
}
const _hoisted_13 = { class: "dropdown-menu" }
const _hoisted_14 = {
  key: 0,
  class: "alert alert-info mt-3 mb-3",
  style: {"white-space":"pre-line"}
}

import { computed, ref, onMounted, watch } from 'vue'
    import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
    import { SavingState } from '@common/Common'
    import EditableValueSeriesSheet from '@common/Components/EditableValueSeriesSheet.vue'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import SaveButton from '@common/Components/SaveButton.vue'
    import Navigation from '@common/Navigation.vue'
    import ValueSeriesTable from '@Quest/_Common/Components/ValueSeriesTable.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'
    import ProjectContextHeader from '@Quest/ProjectPlans/Components/ProjectContextHeader.vue'
    import { useProjectRevenuesQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'
    import { useUpdateProjectRevenuesCommandV1 } from '@Quest/ProjectPlans/commands-vue-composables'
    import type { RevenuesPageReadModel } from '@Quest/ProjectPlans/readmodels'
    import { CurrencyDenomination, TimeFrameGranularity } from '@common/types'

    
export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectRevenuesPage',
  setup(__props) {

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))

    const projectPlanId = computed<string | null>(() => rm.value?.ProjectPlanId ?? null)
    const granularity = computed<TimeFrameGranularity | null>(() => rm.value?.Granularity ?? null)
    const denomination = computed<CurrencyDenomination | null>(() => rm.value?.Denomination ?? null)

    const projectName = computed(() => rm.value?.ProjectName ?? '')
    const projectPlanName = computed<string>(() => rm.value?.ProjectPlanName ?? '')
    const activePlanId = computed<string | null>(() => rm.value?.ActiveProjectPlanId ?? null)

    const userId = computed(() => rm.value?.UserId ?? 0)
    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const isProjectOwner = computed(() => rm.value?.IsProjectOwner ?? false)
    const title = computed(() => `${rm.value?.Title} (${rm.value?.CurrencyLabel})`)

    const hasPhases = computed(() => rm.value?.HasPhases ?? false)

    const inputSeries = computed(() => rm.value?.Series.filter(a => !a.IsTotal) ?? [])
    const isSubmitted = computed(() => rm.value?.IsSubmitted ?? false)
    const comment = computed(() => rm.value?.Comment ?? null)

    const sheet = ref<any>(null)
    const isEditing = ref(false)
    const savingState = ref<SavingState>(SavingState.Saved)
    const stopEditingLabel = computed(() => savingState.value === SavingState.Pending ? 'Cancel editing' : 'Back')
    const editingComment = ref<string | null>(null)

    const {
        runProjectRevenuesQueryV1,
        modelProjectRevenuesQueryV1
    } = useProjectRevenuesQueryV1(workspaceId, tchProjectId, projectId, projectPlanId, granularity, denomination)

    const rm = ref<RevenuesPageReadModel | null>(null)

    const {
        invokeUpdateProjectRevenuesCommandV1,
        onSucceedUpdateProjectRevenuesCommandV1
    } = useUpdateProjectRevenuesCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId, granularity, denomination)


    function startEditing()
    {
        if (!rm.value) return
        editingComment.value = rm.value.Comment
        isEditing.value = true
    }

    function cancelEditing()
    {
        if (savingState.value === SavingState.Saved)
        {
            isEditing.value = false
        }
        else if (
            savingState.value === SavingState.Pending &&
            confirm('You have unsaved changes. Are you sure you want to cancel editing?')
        )
        {
            savingState.value = SavingState.Saved
            isEditing.value = false
        }
    }

    function onDataChange()
    {
        savingState.value = SavingState.Pending
    }

    function handleSaveCommand()
    {
        if (!rm.value) return

        const entries = sheet.value?.getEntriesForSave()

        invokeUpdateProjectRevenuesCommandV1({
            Entries: entries,
            Comment: editingComment.value
        })

        savingState.value = SavingState.InProgress
    }

    function setDenomination(newDenomination: CurrencyDenomination)
    {
        const query = { ...route.query }

        query.denomination = newDenomination

        router.replace({
            params: { ...route.params },
            query
        })

        runProjectRevenuesQueryV1(
            {
                Denomination: newDenomination,
            })
    }

    function setGranularity(newGranularity: TimeFrameGranularity | null)
    {
        const query = { ...route.query }

        if (newGranularity)
            query.granularity = newGranularity
        else
            delete query.granularity

        router.replace({
            params: { ...route.params },
            query
        })

        runProjectRevenuesQueryV1(
            {
                Granularity: newGranularity,
            })
    }


    onMounted(() =>
    {
        document.title = 'Revenues | TransparentChoice'

        runProjectRevenuesQueryV1({
            ProjectPlanId: route.params.planId ? String(route.params.planId) : null,
            Granularity: route.query.granularity as TimeFrameGranularity ?? null,
            Denomination: route.query.denomination as CurrencyDenomination ?? null,
        });
    })

    watch(() => route.params.planId, () =>
    {
        runProjectRevenuesQueryV1({
            ProjectPlanId: route.params.planId as string | null
        })
    })

    watch(modelProjectRevenuesQueryV1, (model) =>
    {
        if (model?.RedirectUrl)
        {
            location.href = model.RedirectUrl
        }
        else
        {
            rm.value = model
        }
    })


    onSucceedUpdateProjectRevenuesCommandV1(() =>
    {
        runProjectRevenuesQueryV1()

        if (savingState.value === SavingState.InProgress)
        {
            savingState.value = SavingState.Saved
        }
    })

    onBeforeRouteLeave((_to, _from, next) =>
    {
        if (savingState.value === SavingState.Pending)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSaveCommand()
                next()
            }
            else
            {
                next()
            }
        }
        else
        {
            next()
        }
    })

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    (isWorkspaceMember.value)
      ? (_openBlock(), _createBlock(PortfolioNavigation, {
          key: 0,
          workspaceId: workspaceId.value,
          projectId: tchProjectId.value
        }, null, 8 /* PROPS */, ["workspaceId", "projectId"]))
      : (isProjectOwner.value)
        ? (_openBlock(), _createBlock(Navigation, { key: 1 }))
        : _createCommentVNode("v-if", true),
    (rm.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(ProjectContextHeader, {
                projectName: projectName.value,
                planId: projectPlanId.value,
                planName: projectPlanName.value,
                activePlanId: activePlanId.value
              }, null, 8 /* PROPS */, ["projectName", "planId", "planName", "activePlanId"])
            ]),
            _createElementVNode("div", _hoisted_4, [
              (isEditing.value)
                ? (_openBlock(), _createBlock(SaveButton, {
                    key: 0,
                    class: "mr-1",
                    savingState: savingState.value,
                    onSaveChanges: _cache[0] || (_cache[0] = ($event: any) => (handleSaveCommand()))
                  }, null, 8 /* PROPS */, ["savingState"]))
                : _createCommentVNode("v-if", true),
              (isEditing.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    class: "btn btn-outline-secondary",
                    style: {"padding":"0.7em 2em"},
                    onClick: cancelEditing
                  }, _toDisplayString(stopEditingLabel.value), 1 /* TEXT */))
                : _createCommentVNode("v-if", true),
              _createCommentVNode(" When NOT submitted "),
              (!isEditing.value && !isSubmitted.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    class: "btn btn-success mr-1",
                    style: {"padding":"0.7em 2em"},
                    onClick: startEditing
                  }, " Edit revenues "))
                : _createCommentVNode("v-if", true),
              _createCommentVNode(" When submitted "),
              (!isEditing.value && isSubmitted.value)
                ? (_openBlock(), _createElementBlock("button", _hoisted_5, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("i", { class: "fas fa-lock mr-2" }, null, -1 /* CACHED */),
                    _createTextVNode(" Submitted ", -1 /* CACHED */)
                  ]))]))
                : _createCommentVNode("v-if", true)
            ])
          ]),
          _cache[12] || (_cache[12] = _createElementVNode("hr", null, null, -1 /* CACHED */)),
          _createVNode(ProjectNavigation, {
            ActivePage: "revenues",
            class: "mt-4",
            workspaceId: workspaceId.value,
            tchProjectId: tchProjectId.value,
            projectId: projectId.value,
            planId: projectPlanId.value
          }, null, 8 /* PROPS */, ["workspaceId", "tchProjectId", "projectId", "planId"]),
          (isEditing.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _cache[6] || (_cache[6] = _createElementVNode("label", {
                    for: "revenues-comment",
                    class: "mb-1 text-muted",
                    style: {"font-weight":"400","font-size":"0.97em"}
                  }, " Assumptions or notes (optional) ", -1 /* CACHED */)),
                  _withDirectives(_createElementVNode("textarea", {
                    id: "revenues-comment",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editingComment).value = $event)),
                    onInput: onDataChange,
                    rows: "3",
                    class: "form-control"
                  }, "                    ", 544 /* NEED_HYDRATION, NEED_PATCH */), [
                    [
                      _vModelText,
                      editingComment.value,
                      void 0,
                      { trim: true }
                    ]
                  ])
                ]),
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "alert alert-warning mb-1" }, [
                  _createElementVNode("strong", null, "Note:"),
                  _createTextVNode(" Formula entered below will save as values. ")
                ], -1 /* CACHED */)),
                _createVNode(EditableValueSeriesSheet, {
                  ref_key: "sheet",
                  ref: sheet,
                  propTitle: title.value,
                  propSeries: inputSeries.value,
                  propColumns: rm.value.Columns,
                  onDataChange: onDataChange
                }, null, 8 /* PROPS */, ["propTitle", "propSeries", "propColumns"])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", null, [
                    _createElementVNode("div", _hoisted_10, [
                      _cache[9] || (_cache[9] = _createElementVNode("span", { class: "btn btn-secondary disabled" }, " Revenues ", -1 /* CACHED */)),
                      _createVNode(_component_router_link, {
                        to: {name: 'q-project-margins-path', params: { workspaceId: workspaceId.value, tchProjectId: tchProjectId.value, projectId: projectId.value, planId: projectPlanId.value }, query: _unref(route).query },
                        class: "btn btn-outline-secondary"
                      }, {
                        default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                          _createTextVNode(" Margins ", -1 /* CACHED */)
                        ]))]),
                        _: 1 /* STABLE */
                      }, 8 /* PROPS */, ["to"])
                    ]),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("button", _hoisted_12, _toDisplayString(denomination.value) + "s ", 1 /* TEXT */),
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("a", {
                          class: "dropdown-item",
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (setDenomination('Million')))
                        }, "Millions"),
                        _createElementVNode("a", {
                          class: "dropdown-item",
                          onClick: _cache[3] || (_cache[3] = ($event: any) => (setDenomination('Thousand')))
                        }, "Thousands"),
                        _createElementVNode("a", {
                          class: "dropdown-item",
                          onClick: _cache[4] || (_cache[4] = ($event: any) => (setDenomination('Unit')))
                        }, "Units")
                      ])
                    ]),
                    _createCommentVNode("<div class=\"dropdown d-inline mr-1\">\n                            <button class=\"btn btn-outline-secondary dropdown-toggle\" type=\"button\" data-toggle=\"dropdown\">\n                                {{granularity ?? 'By Phase'}}\n                            </button>\n                            <div class=\"dropdown-menu\">\n                                <a class=\"dropdown-item\" @click=\"setGranularity('Monthly')\">Monthly</a>\n                                <a class=\"dropdown-item\" @click=\"setGranularity('Quarterly')\">Quarterly</a>\n                                <a class=\"dropdown-item\" @click=\"setGranularity('Yearly')\">Yearly</a>\n                                <a v-if=\"hasPhases\" class=\"dropdown-item\" @click=\"setGranularity()\">By Phase</a>\n                            </div>\n                        </div>")
                  ]),
                  _cache[10] || (_cache[10] = _createElementVNode("div", null, null, -1 /* CACHED */))
                ]),
                (comment.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _cache[11] || (_cache[11] = _createElementVNode("strong", null, "Assumptions / Notes:", -1 /* CACHED */)),
                      _createTextVNode(" " + _toDisplayString(comment.value), 1 /* TEXT */)
                    ]))
                  : _createCommentVNode("v-if", true),
                _createVNode(ValueSeriesTable, {
                  propTitle: title.value,
                  propSeries: rm.value.Series,
                  propColumns: rm.value.Columns,
                  propStartDate: rm.value.ProjectStartDate
                }, null, 8 /* PROPS */, ["propTitle", "propSeries", "propColumns", "propStartDate"])
              ]))
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})